import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://www.museshaircwb.com';

const schemaJsonLd = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "WebPage",
      "@id": `${SITE_URL}/contact#webpage`,
      "url": `${SITE_URL}/contact`,
      "name": "Contact | Muses Hair CWB - Book Hair & Scalp Treatment Consultation Causeway Bay",
      "description": "Book your professional hair and scalp consultation at Muses Hair CWB in Causeway Bay, Hong Kong. WhatsApp booking available. Serving international clients and expats.",
      "inLanguage": "en",
      "isPartOf": {
        "@type": "WebSite",
        "@id": `${SITE_URL}/#website`
      },
      "breadcrumb": {
        "@type": "BreadcrumbList",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Home",
            "item": SITE_URL
          },
          {
            "@type": "ListItem",
            "position": 2,
            "name": "Contact",
            "item": `${SITE_URL}/contact`
          }
        ]
      }
    },
    {
      "@type": "LocalBusiness",
      "@id": `${SITE_URL}/contact#localbusiness`,
      "name": "Muses Hair CWB",
      "image": "https://www.museshaircwb.com/wp-content/uploads/2024/01/Muses-Hair-Salon-Causeway-Bay-Hong-Kong-scaled.jpg",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "Causeway Bay",
        "addressLocality": "Causeway Bay",
        "addressRegion": "Hong Kong",
        "addressCountry": "HK"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": 22.2800,
        "longitude": 114.1840
      },
      "url": SITE_URL,
      "telephone": "+852-9876-5432",
      "email": "info@museshaircwb.com",
      "openingHoursSpecification": [
        {
          "@type": "OpeningHoursSpecification",
          "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
          "opens": "10:00",
          "closes": "20:00"
        },
        {
          "@type": "OpeningHoursSpecification",
          "dayOfWeek": ["Saturday", "Sunday"],
          "opens": "10:00",
          "closes": "19:00"
        }
      ],
      "priceRange": "HK$580 – HK$880"
    }
  ]
};

export default function ContactPage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const scriptId = 'schema-jsonld-contact';
    let script = document.getElementById(scriptId) as HTMLScriptElement | null;
    if (!script) {
      script = document.createElement('script');
      script.id = scriptId;
      script.type = 'application/ld+json';
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(schemaJsonLd);
    return () => {
      const el = document.getElementById(scriptId);
      if (el) el.remove();
    };
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    try {
      const response = await fetch('https://readdy.ai/api/form/d6lq6r14a3uojp5fgfbg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formData as any).toString(),
      });

      if (response.ok) {
        setFormSubmitted(true);
        form.reset();
        setTimeout(() => setFormSubmitted(false), 5000);
      }
    } catch (error) {
      console.error('Form submission error:', error);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-[#F3F0EB]/98 backdrop-blur-sm shadow-sm' : 'bg-[#F3F0EB]'
      }`}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center">
            <Link to="/">
              <img 
                src="https://www.museshaircwb.com/wp-content/uploads/2024/01/cropped-Muses-Hair-Logo-1.png" 
                alt="Muses Hair CWB" 
                className="h-11 w-auto cursor-pointer"
              />
            </Link>
          </div>
          <div className="hidden md:flex items-center gap-8">
            <Link to="/" className="text-sm font-medium text-gray-800 hover:text-gray-600 transition-colors">Home</Link>
            <Link to="/about" className="text-sm font-medium text-gray-800 hover:text-gray-600 transition-colors">About</Link>
            <Link to="/qualifications" className="text-sm font-medium text-gray-800 hover:text-gray-600 transition-colors">Qualifications</Link>
            <Link to="/services" className="text-sm font-medium text-gray-800 hover:text-gray-600 transition-colors">Services</Link>
            <Link to="/products" className="text-sm font-medium text-gray-800 hover:text-gray-600 transition-colors">Products</Link>
            <Link to="/testimonials" className="text-sm font-medium text-gray-800 hover:text-gray-600 transition-colors">Testimonials</Link>
            <Link to="/contact" className="text-sm font-medium text-gray-900 transition-colors">Contact</Link>
          </div>
          <a 
            href="https://wa.me/message/YAZZ2YL77SHLN1" 
            target="_blank" 
            rel="noopener noreferrer"
            className="bg-gray-800 hover:bg-gray-700 text-white px-6 py-2.5 rounded-md text-sm font-medium transition-all whitespace-nowrap cursor-pointer"
          >
            WhatsApp Us
          </a>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-6 bg-[#F3F0EB]">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-block border border-gray-400 text-gray-700 px-4 py-1.5 rounded-full text-xs uppercase tracking-wider mb-8">
            Get In Touch
          </div>
          <h1 className="font-serif text-5xl md:text-6xl text-gray-900 mb-6 leading-tight" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            Book Your Professional<br />Consultation
          </h1>
          <p className="text-gray-700 text-lg leading-relaxed max-w-2xl mx-auto">
            Ready to solve your hair and scalp concerns? Reach out to schedule a personalized consultation at our Causeway Bay location.
          </p>
        </div>
      </section>

      {/* Contact Methods Section */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-3 gap-8 mb-20">
            <div className="bg-[#F9F8F6] rounded-lg p-8 text-center">
              <div className="w-16 h-16 flex items-center justify-center bg-gray-800 rounded-full mx-auto mb-6">
                <i className="ri-whatsapp-line text-3xl text-white"></i>
              </div>
              <h3 className="font-serif text-2xl text-gray-900 mb-3" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                WhatsApp
              </h3>
              <p className="text-gray-600 mb-6 leading-relaxed">
                Quick and convenient booking via WhatsApp
              </p>
              <a 
                href="https://wa.me/message/YAZZ2YL77SHLN1" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#20BA5A] text-white px-6 py-3 rounded-md text-sm font-medium transition-all whitespace-nowrap cursor-pointer"
              >
                <i className="ri-whatsapp-line text-lg"></i>
                Message Us
              </a>
            </div>

            <div className="bg-[#F9F8F6] rounded-lg p-8 text-center">
              <div className="w-16 h-16 flex items-center justify-center bg-gray-800 rounded-full mx-auto mb-6">
                <i className="ri-phone-line text-3xl text-white"></i>
              </div>
              <h3 className="font-serif text-2xl text-gray-900 mb-3" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                Phone
              </h3>
              <p className="text-gray-600 mb-6 leading-relaxed">
                Call us directly for immediate assistance
              </p>
              <a 
                href="tel:+85298765432" 
                className="inline-block text-gray-800 hover:text-gray-600 font-medium text-lg transition-colors cursor-pointer"
              >
                +852 9876 5432
              </a>
            </div>

            <div className="bg-[#F9F8F6] rounded-lg p-8 text-center">
              <div className="w-16 h-16 flex items-center justify-center bg-gray-800 rounded-full mx-auto mb-6">
                <i className="ri-mail-line text-3xl text-white"></i>
              </div>
              <h3 className="font-serif text-2xl text-gray-900 mb-3" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                Email
              </h3>
              <p className="text-gray-600 mb-6 leading-relaxed">
                Send us your inquiry anytime
              </p>
              <a 
                href="mailto:info@museshaircwb.com" 
                className="inline-block text-gray-800 hover:text-gray-600 font-medium transition-colors cursor-pointer"
              >
                info@museshaircwb.com
              </a>
            </div>
          </div>

          {/* Location & Hours */}
          <div className="grid md:grid-cols-2 gap-16 items-start">
            <div>
              <h2 className="font-serif text-4xl text-gray-900 mb-8" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                Visit Our Salon
              </h2>
              
              <div className="space-y-8">
                <div>
                  <div className="flex items-start gap-4 mb-4">
                    <div className="w-12 h-12 flex items-center justify-center bg-gray-800 rounded-full flex-shrink-0">
                      <i className="ri-map-pin-line text-xl text-white"></i>
                    </div>
                    <div>
                      <h3 className="font-serif text-xl text-gray-900 mb-2" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                        Location
                      </h3>
                      <p className="text-gray-700 leading-relaxed">
                        Causeway Bay<br />
                        Hong Kong
                      </p>
                    </div>
                  </div>
                </div>

                <div>
                  <div className="flex items-start gap-4 mb-4">
                    <div className="w-12 h-12 flex items-center justify-center bg-gray-800 rounded-full flex-shrink-0">
                      <i className="ri-time-line text-xl text-white"></i>
                    </div>
                    <div>
                      <h3 className="font-serif text-xl text-gray-900 mb-3" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                        Opening Hours
                      </h3>
                      <div className="space-y-2 text-gray-700">
                        <div className="flex justify-between">
                          <span className="font-medium">Monday - Friday</span>
                          <span>10:00 AM - 8:00 PM</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="font-medium">Saturday - Sunday</span>
                          <span>10:00 AM - 7:00 PM</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div>
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 flex items-center justify-center bg-gray-800 rounded-full flex-shrink-0">
                      <i className="ri-train-line text-xl text-white"></i>
                    </div>
                    <div>
                      <h3 className="font-serif text-xl text-gray-900 mb-2" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                        How to Get Here
                      </h3>
                      <p className="text-gray-700 leading-relaxed">
                        <strong>MTR:</strong> Causeway Bay Station, Exit E<br />
                        5 minutes walk from the station<br />
                        <strong>Bus:</strong> Multiple routes stop nearby
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-10 pt-10 border-t border-gray-200">
                <h3 className="font-serif text-xl text-gray-900 mb-4" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                  Follow Us
                </h3>
                <div className="flex gap-4">
                  <a 
                    href="https://www.instagram.com/museshaircwb" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="w-12 h-12 flex items-center justify-center bg-gray-800 hover:bg-gray-700 rounded-full transition-colors cursor-pointer"
                  >
                    <i className="ri-instagram-line text-xl text-white"></i>
                  </a>
                  <a 
                    href="https://www.facebook.com/museshaircwb" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="w-12 h-12 flex items-center justify-center bg-gray-800 hover:bg-gray-700 rounded-full transition-colors cursor-pointer"
                  >
                    <i className="ri-facebook-line text-xl text-white"></i>
                  </a>
                  <a 
                    href="https://wa.me/message/YAZZ2YL77SHLN1" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="w-12 h-12 flex items-center justify-center bg-[#25D366] hover:bg-[#20BA5A] rounded-full transition-colors cursor-pointer"
                  >
                    <i className="ri-whatsapp-line text-xl text-white"></i>
                  </a>
                </div>
              </div>
            </div>

            {/* Booking Form */}
            <div className="bg-[#F9F8F6] rounded-lg p-10">
              <h2 className="font-serif text-3xl text-gray-900 mb-6" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                Request a Consultation
              </h2>
              <p className="text-gray-600 mb-8 leading-relaxed">
                Fill out the form below and we'll get back to you within 24 hours to confirm your appointment.
              </p>

              {formSubmitted && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                  <div className="flex items-center gap-3">
                    <i className="ri-checkbox-circle-line text-2xl text-green-600"></i>
                    <div>
                      <p className="text-green-800 font-medium">Thank you for your booking request!</p>
                      <p className="text-green-700 text-sm">We'll contact you shortly to confirm your appointment.</p>
                    </div>
                  </div>
                </div>
              )}

              <form onSubmit={handleSubmit} data-readdy-form id="contact-booking-form">
                <div className="space-y-5">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      className="w-full px-4 py-3 bg-white border border-gray-300 rounded-md text-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-gray-800 focus:border-transparent"
                      placeholder="Your name"
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      className="w-full px-4 py-3 bg-white border border-gray-300 rounded-md text-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-gray-800 focus:border-transparent"
                      placeholder="your@email.com"
                    />
                  </div>

                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                      Phone Number *
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      required
                      className="w-full px-4 py-3 bg-white border border-gray-300 rounded-md text-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-gray-800 focus:border-transparent"
                      placeholder="+852 1234 5678"
                    />
                  </div>

                  <div>
                    <label htmlFor="service" className="block text-sm font-medium text-gray-700 mb-2">
                      Service Interest *
                    </label>
                    <select
                      id="service"
                      name="service"
                      required
                      className="w-full px-4 py-3 bg-white border border-gray-300 rounded-md text-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-gray-800 focus:border-transparent cursor-pointer"
                    >
                      <option value="">Select a service</option>
                      <option value="scalp-consultation">Scalp Health Consultation</option>
                      <option value="hair-cutting">Hair Cutting</option>
                      <option value="hair-coloring">Hair Coloring</option>
                      <option value="keratin-smoothing">Keratin Smoothing</option>
                      <option value="hair-repair">Hair Repair Treatment</option>
                      <option value="scalp-treatment">Scalp Treatment</option>
                      <option value="general-consultation">General Consultation</option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor="preferred-date" className="block text-sm font-medium text-gray-700 mb-2">
                      Preferred Date
                    </label>
                    <input
                      type="date"
                      id="preferred-date"
                      name="preferred-date"
                      className="w-full px-4 py-3 bg-white border border-gray-300 rounded-md text-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-gray-800 focus:border-transparent cursor-pointer"
                    />
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">
                      Message or Concerns
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      rows={4}
                      maxLength={500}
                      className="w-full px-4 py-3 bg-white border border-gray-300 rounded-md text-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-gray-800 focus:border-transparent resize-none"
                      placeholder="Tell us about your hair or scalp concerns... (max 500 characters)"
                    ></textarea>
                  </div>

                  <button
                    type="submit"
                    className="w-full bg-gray-800 hover:bg-gray-700 text-white px-8 py-4 rounded-md text-base font-medium transition-all whitespace-nowrap cursor-pointer"
                  >
                    Submit Booking Request
                  </button>

                  <p className="text-gray-500 text-xs text-center">
                    By submitting this form, you agree to be contacted regarding your consultation request.
                  </p>
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-20 px-6 bg-[#F3F0EB]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-serif text-4xl text-gray-900 mb-4" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              Find Us in Causeway Bay
            </h2>
            <p className="text-gray-600 text-lg">
              Conveniently located in the heart of Causeway Bay, Hong Kong
            </p>
          </div>
          <div className="rounded-lg overflow-hidden shadow-lg" style={{ height: '500px' }}>
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3691.8234567890123!2d114.18400000000001!3d22.28!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMjLCsDE2JzQ4LjAiTiAxMTTCsDExJzAyLjQiRQ!5e0!3m2!1sen!2shk!4v1234567890123!5m2!1sen!2shk"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Muses Hair CWB Location"
            ></iframe>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-6 py-16">
          <div className="grid md:grid-cols-4 gap-12 mb-12">
            <div>
              <img 
                src="https://www.museshaircwb.com/wp-content/uploads/2024/01/cropped-Muses-Hair-Logo-1.png" 
                alt="Muses Hair CWB" 
                className="h-11 w-auto mb-6 brightness-0 invert"
              />
              <p className="text-gray-400 text-sm leading-relaxed">
                Professional solution center for hair and scalp problems in Causeway Bay, Hong Kong.
              </p>
            </div>

            <div>
              <h4 className="text-xs uppercase tracking-wider text-gray-400 mb-6">Quick Links</h4>
              <ul className="space-y-3">
                <li><Link to="/" className="text-gray-300 hover:text-white transition-colors text-sm cursor-pointer">Home</Link></li>
                <li><Link to="/about" className="text-gray-300 hover:text-white transition-colors text-sm cursor-pointer">About</Link></li>
                <li><Link to="/qualifications" className="text-gray-300 hover:text-white transition-colors text-sm cursor-pointer">Qualifications</Link></li>
                <li><Link to="/services" className="text-gray-300 hover:text-white transition-colors text-sm cursor-pointer">Services</Link></li>
              </ul>
            </div>

            <div>
              <h4 className="text-xs uppercase tracking-wider text-gray-400 mb-6">More</h4>
              <ul className="space-y-3">
                <li><Link to="/products" className="text-gray-300 hover:text-white transition-colors text-sm cursor-pointer">Products</Link></li>
                <li><Link to="/testimonials" className="text-gray-300 hover:text-white transition-colors text-sm cursor-pointer">Testimonials</Link></li>
                <li><Link to="/contact" className="text-gray-300 hover:text-white transition-colors text-sm cursor-pointer">Contact</Link></li>
              </ul>
            </div>

            <div>
              <h4 className="text-xs uppercase tracking-wider text-gray-400 mb-6">Connect</h4>
              <ul className="space-y-3">
                <li><a href="https://www.instagram.com/museshaircwb" target="_blank" rel="noopener noreferrer" className="text-gray-300 hover:text-white transition-colors text-sm cursor-pointer">Instagram</a></li>
                <li><a href="https://www.facebook.com/museshaircwb" target="_blank" rel="noopener noreferrer" className="text-gray-300 hover:text-white transition-colors text-sm cursor-pointer">Facebook</a></li>
                <li><a href="https://wa.me/message/YAZZ2YL77SHLN1" target="_blank" rel="noopener noreferrer" className="text-gray-300 hover:text-white transition-colors text-sm cursor-pointer">WhatsApp</a></li>
              </ul>
            </div>
          </div>

          <div className="pt-8 border-t border-gray-800 text-center">
            <p className="text-gray-400 text-sm">
              © 2024 Muses Hair CWB. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}